import { QRCodeSVG } from 'qrcode.react';
import { isNil } from 'rambdax';
import { useEffect, useState } from 'react';

import type { PortfolioItemResponse } from '@ping/api';
import { useDepositWallet, useGetAllAsset, useGetPortfolio } from '@ping/api';
import CopyIcon from '@ping/assets/Icon/copy.svg';
import { NetworkSelect } from '@ping/components/CryptoTransaction/NetworkSelect';
//import { DEFAULT_MARKET_QUOTE } from '@ping/configs';
import { t } from '@ping/helpers';
import { isDepositWithdrawAssetListed, isDepositNetworkDisabled } from '@ping/helpers/deposit-withdraw-access.helper';
import { useDesktopDevice, useSelectedCurrency } from '@ping/hooks';
import { AssetSelect, Compact, Copyable, Divider, Text } from '@ping/uikit';
import { DateTime, format } from '@ping/utils';

import { DepositErrorAlert } from './DepositErrorAlert';
import style from './style.module.scss';

type IDepositProps = {
  assetId?: string;
};

interface IAsset {
  address: string;
  qrUrl: string;
  id: string;
  amount: number;
  balance: number;
  confirmationCount: number | undefined;
}

const INITIAL_ASSET: IAsset = {
  address: '',
  qrUrl: '',
  id: '',
  amount: 0,
  balance: 0,
  confirmationCount: undefined,
};

export function ReceiveTransaction(props: IDepositProps) {
  const selectedCurrency = useSelectedCurrency();
  const [assetInfo, setAssetInfo] = useState<IAsset>({ ...INITIAL_ASSET, id: props.assetId?.toUpperCase() });
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<{ message?: string; errorCode?: number } | null>(null);

  const handleCompactClick = () => {
    setExpanded(!expanded);
  };

  const { mutate: mutateRequestFundsDepositWallet, isLoading: isLoadingRequestFundsDepositWallet } = useDepositWallet();

  const { data: portfolio, isLoading: isPortfolioLoading } = useGetPortfolio({
    query: {
      staleTime: DateTime.minutes(5),
      select: ({ portfolioItems }) => {
        const assetsMap = portfolioItems?.reduce(
          (map, item) => map.set(item.assetId.toUpperCase(), item),
          new Map<string, PortfolioItemResponse>()
        );

        return { assetsMap };
      },
    },
  });

  const { isLoading: isAssetsLoading } = useGetAllAsset();

  const isDesktop = useDesktopDevice();

  useEffect(() => handleOnSelectAsset(assetInfo.id), [isPortfolioLoading, isAssetsLoading]);

  const handleOnSelectAsset = (assetId: string) => {
    setSelectedNetwork(undefined);
    setError(null);
    if (isPortfolioLoading || isAssetsLoading || !assetId?.length) return;
    const { amount, balance } = portfolio.assetsMap.get(assetId.toUpperCase()) || {};

    setAssetInfo({
      address: INITIAL_ASSET.address,
      id: assetId.toUpperCase(),
      qrUrl: assetInfo.qrUrl,
      amount: amount || INITIAL_ASSET.amount,
      balance: balance || INITIAL_ASSET.balance,
      confirmationCount: INITIAL_ASSET.confirmationCount,
    });
  };

  const handleNetworkChange = (network, networkConfig) => {
    setSelectedNetwork(network);
    setError(null);
    mutateRequestFundsDepositWallet(
      { data: { assetId: assetInfo.id.toLowerCase(), paymentSystem: network } },
      {
        onSuccess: deposit => {
          setAssetInfo({
            address: deposit.address,
            qrUrl: deposit.qrUrl,
            id: assetInfo.id.toUpperCase(),
            amount: assetInfo.amount || INITIAL_ASSET.amount,
            balance: assetInfo.balance || INITIAL_ASSET.balance,
            confirmationCount: networkConfig.confirmation_count || INITIAL_ASSET.confirmationCount,
          });
          setExpanded(false);
          setError(null);
        },
        onError: (error: any) => {
          const errorData = error?.response?.data;
          const errorMessage =
            errorData?.errors?.[0]?.message || errorData?.message || t('Failed to generate deposit address');
          setError({
            message: errorMessage,
            errorCode: errorData?.errorCode,
          });
        },
      }
    );
  };

  return (
    <div
      className={style['receive-transaction']}
      data-deposit-address-loading={isLoadingRequestFundsDepositWallet}
      data-is-asset-selected={Boolean(assetInfo.id.length)}
      data-is-network-selected={!isNil(selectedNetwork)}
    >
      <Divider className={style['receive-transaction__divider']} />
      <div className={style['receive-transaction__asset']}>
        <div>
          <Text body='regular' className={style['receive-transaction__coin-select-label']}>
            {t('Currency')}
          </Text>

          <AssetSelect onChange={handleOnSelectAsset} assetId={assetInfo.id} filterFn={isDepositWithdrawAssetListed} />
        </div>
        <Text caption='regular' className={style['receive-transaction__asset-balance']}>
          {`${assetInfo.id} Balance: ${format.crypto(assetInfo.amount, assetInfo.id)}`}
          {Boolean(assetInfo.amount) && ` (${format.fiat(assetInfo.balance, selectedCurrency)})`}
        </Text>
      </div>

      <section id={style['networks-dropdown']}>
        <Text body='regular' className={style['receive-transaction__coin-select-label']}>
          {t('Network')}
        </Text>

        <NetworkSelect
          assetId={assetInfo.id.toLowerCase()}
          selectedKey={selectedNetwork}
          onChange={handleNetworkChange}
          ignore={network => isDepositNetworkDisabled(network)}
        />
      </section>

      {error && <DepositErrorAlert errorMessage={error.message} />}

      {!error && assetInfo.address && (
        <>
          <section className={style['receive-transaction__address-section']}>
            <div className={style['receive-transaction__qr-code-wrapper']}>
              <QRCodeSVG
                className={style['receive-transaction__qr-code']}
                size={136}
                value={decodeURIComponent(assetInfo.qrUrl)}
                level='M'
              />
            </div>

            <div className={style['receive-transaction__address-details-wrapper']}>
              <div className={style['receive-transaction__address-details']}>
                <Text className={style['receive-transaction__address-title']} body='semi-bold'>
                  {t(`${assetInfo.id} address`)}
                </Text>

                <Copyable
                  className={style['receive-transaction__address-copy']}
                  title={t('Wallet address')}
                  text={assetInfo.address}
                >
                  <CopyIcon />
                </Copyable>

                <Text className={style['receive-transaction__address-text']} body='regular'>
                  <Compact
                    text={assetInfo.address}
                    middle
                    visibleCharacterCount={15}
                    isExpanded={expanded}
                    onClick={handleCompactClick}
                  />
                </Text>
              </div>
              {!isDesktop && (
                <div className={style['receive-transaction__button-block']}>
                  <a className={style['receive-transaction__button-block-button']} href={assetInfo.qrUrl}>
                    {t('Deposit via wallet')}
                  </a>
                  <Text caption='regular' grayColor>
                    {t('The deposit button allows you to quickly deposit assets into your Ping account.')}
                  </Text>
                </div>
              )}
            </div>
          </section>

          <section className={style['receive-transaction__info-section']}>
            <Text className={style['receive-transaction__info-text']} caption='regular'>
              {t('Expected arrival')}
            </Text>
            <Text className={style['receive-transaction__info-text']} caption='regular'>
              {t(`${assetInfo.confirmationCount} network confirmation`)}
            </Text>
          </section>

          <section className={style['receive-transaction__info-section']}>
            <Text className={style['receive-transaction__info-text']} caption='regular'>
              {t('Minimum deposit')}
            </Text>
            <Text className={style['receive-transaction__info-text']} caption='regular'>
              {/* TODO: Minimum deposit */}
              {`0.00001 ${assetInfo.id}`}
            </Text>
          </section>

          <section className={style['receive-transaction__footer-section']}>
            <Text className={style['receive-transaction__footer-text']} caption='regular'>
              {t(`Please remember to send only ${assetInfo.id} to this deposit address`)}
            </Text>
          </section>
        </>
      )}
    </div>
  );
}
